import type { Client } from "shared"
import { DEFAULT_AVAILABLE_CUSTOM_TAGS, useUpdateableHandler, clientApi, CardView } from "shared"
import { InlineLabelSwitch } from "shared/src/components/shadcn/components/atoms/switches"
import { Tooltip, TooltipTrigger, TooltipContent } from "shared/src/components/shadcn/components/ui/tooltip"
import { Button } from "shared/src/components/shadcn/components/atoms/buttons"
import { useState } from "react"
import "./TagsPage.css"

export function TagsPage({
	clientId,
	data,
	setData,
}: Readonly<{
	clientId: string
	data: Partial<Client>
	setData: (data: Partial<Client>) => void
}>): JSX.Element {
	const tagEntries = Object.entries(data?.options?.customTags?.tags ?? {})
	const [localAvailableTags, handleLocalAvailableTags] = useUpdateableHandler(
		data?.limits?.tags?.limit ?? data?.options?.customTags?.availableTags ?? DEFAULT_AVAILABLE_CUSTOM_TAGS
	)
	const [localEnforceTagLimit, setLocalEnforceTagLimit] = useState(data?.limits?.tags?.enforced ?? false)
	const [isLoading, setIsLoading] = useState(false)

	const updateTagLimit = (updates) => {
		setIsLoading(true)
		const newLimit = {
			...data.limits,
			tags: {
				...data?.limits?.tags,
				...updates,
			},
		}
		const change = {
			limits: {
				tags: {
					...updates,
				},
			},
		}

		const newData = { limits: newLimit }
		clientApi
			.patch({ clientId, updates: change, changesOnly: true })
			.then(() => {
				setData({ ...data, ...newData })
			})
			.catch(() => {
				alert("Failed to update. Refresh page to check current data")
			})
			.finally(() => {
				setIsLoading(false)
			})
	}

	const setNewTagLimit = () => {
		updateTagLimit({ limit: localAvailableTags })
	}

	const setEnforceTagLimit = (newValue: boolean) => {
		updateTagLimit({ enforced: newValue, limit: localAvailableTags })
		setLocalEnforceTagLimit(newValue)
	}
	return (
		<div className="admin-tags-page">
			<div style={{ margin: 20 }}>
				<h3 className="bs-h3">Available Tags:</h3>
				<div style={{ display: "flex", gap: 5, alignItems: "center", margin: "20px 0" }}>
					<span style={tagEntries.length > localAvailableTags ? { color: "var(--ara-error)" } : {}}>
						Tags Used: {tagEntries.length}
					</span>{" "}
					/
					<input
						style={{ width: 40 }}
						type="text"
						inputMode="numeric"
						pattern="[0-9]*"
						value={localAvailableTags}
						onChange={handleLocalAvailableTags}></input>
					<Button loading={isLoading} onClick={setNewTagLimit}>
						Set New Limit
					</Button>
				</div>
				<div style={{ width: "fit-content" }}>
					<Tooltip>
						<TooltipTrigger asChild>
							<div style={{ display: "flex", gap: 10, alignItems: "center", margin: "20px 0", width: "fit-content" }}>
								Enforce tag limits:{" "}
								<InlineLabelSwitch checked={localEnforceTagLimit} onCheckedChange={setEnforceTagLimit} />
							</div>
						</TooltipTrigger>
						<TooltipContent side="bottom" className="max-w-[300px]">
							Users will not be able to add more tags than the limit set here. Tags that are already added will not be
							removed.
						</TooltipContent>
					</Tooltip>
				</div>
				<h3 className="bs-h3">Existing Tags:</h3>
				<div style={{ display: "flex", flexDirection: "column", gap: 20, margin: "20px 0" }}>
					{tagEntries.map(([tagName, tag]) => (
						<CardView key={tagName}>
							<h4 className="bs-h4">{tag.displayName}</h4>
							<h5 className="bs-h5">Description:</h5>
							<p>{tag.description ?? ""}</p>
							<h5 className="bs-h5">Prompt:</h5>
							<p>{tag.prompt}</p>
							{tag.promptOptions != null && (
								<div style={{ display: "flex", gap: 15 }}>
									{Object.entries(tag.promptOptions ?? {}).map(([option, value]) => (
										<div key={option} style={{ display: "flex", gap: 5 }}>
											<div style={{ fontWeight: 500, textTransform: "capitalize" }}>{option}:</div>
											<div>{value?.toString()}</div>
										</div>
									))}
								</div>
							)}
						</CardView>
					))}
				</div>
			</div>
		</div>
	)
}
